import React, { ChangeEvent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Combobox,
  ComboboxOption,
  Flex,
  GridLayout,
  SingleSelect,
  SingleSelectOption,
  Typography,
} from "@strapi/design-system"
import { debounce } from "radash"
import PageLoader from "components/PageLoader"
import { UICustomizationData } from "pages/UICustomization/types.ts"
import { getFontsTemplate } from "./fontsTemplate"
import { useFonts } from "hooks/query/useFontsQuery"

interface FontsListProps {
  fontsList: string[]
  value: string
}

const FontsList = React.memo(({ fontsList, value }: FontsListProps) => {
  const list =
    !value || value.length === 0 || value === ""
      ? fontsList
      : fontsList.filter((name) => name.toLowerCase().includes(value.toLowerCase()))
  return (
    <>
      {list.slice(0, 7).map((fontName) => (
        <ComboboxOption value={fontName} key={fontName}>
          {fontName}
        </ComboboxOption>
      ))}
    </>
  )
})

interface UICustomizationFontsProps {
  data: UICustomizationData
  onChange: (data: UICustomizationData) => void
}

const safeObject = (obj: any) => {
  return obj ?? {}
}

export const UICustomizationFonts = ({ data, onChange }: UICustomizationFontsProps) => {
  const { t } = useTranslation()
  const { data: fontsData, isLoading: loading, isError } = useFonts()
  const fontsList = useMemo(() => Object.keys(safeObject(fontsData)), [fontsData])

  const translatedFontsTemplate = useMemo(() => getFontsTemplate(t), [t])

  const [inputValues, setInputValues] = useState<Record<string, string>>(() => {
    const initialValues: Record<string, string> = {}
    translatedFontsTemplate.forEach(({ keyFamily }) => {
      initialValues[keyFamily] = data.styles[keyFamily] || ""
    })
    return initialValues
  })

  const debouncedOnChange = debounce({ delay: 100 }, (newData: UICustomizationData) => {
    onChange(newData)
  })

  const handleInput = (keyFamily: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValues((prev) => ({ ...prev, [keyFamily]: value }))
    debouncedOnChange({
      ...data,
      styles: {
        ...data.styles,
        [keyFamily]: value,
      },
    })
  }

  const handleChange = (keyFamily: string) => (item: string) => {
    setInputValues((prev) => ({ ...prev, [keyFamily]: item }))
    debouncedOnChange({
      ...data,
      styles: {
        ...data.styles,
        [keyFamily]: item,
      },
    })
  }

  if (isError)
    return (
      <Typography variant="beta" textColor="danger600">
        {t("fetchError")}
      </Typography>
    )

  return (
    <>
      {loading ? (
        <PageLoader />
      ) : (
        <GridLayout>
          {translatedFontsTemplate.map(({ title, subTitle, keyFamily, keyStyle, keyWeight }) => (
            <Box padding={6} background="neutral0" hasRadius key={title}>
              <Flex gap={2} direction="column" alignItems="">
                <Typography variant="beta">{title}</Typography>
                <Typography variant="omega" textColor="neutral300">
                  {subTitle}
                </Typography>
                <Combobox
                  label={t("uICustomizationFonts.fontFamily")}
                  value={inputValues[keyFamily]}
                  error={!inputValues[keyFamily]}
                  onChange={handleChange(keyFamily)}
                  onInput={handleInput(keyFamily)}
                  autocomplete="contains"
                >
                  <FontsList fontsList={fontsList} value={inputValues[keyFamily]} />{" "}
                </Combobox>
                {data.styles[keyFamily] && safeObject(fontsData[data.styles[keyFamily]]) && (
                  <SingleSelect
                    label={t("uICustomizationFonts.fontStyle")}
                    value={data.styles[keyStyle]}
                    onChange={(item) =>
                      debouncedOnChange({
                        ...data,
                        styles: {
                          ...data.styles,
                          [keyStyle]: item,
                        },
                      })
                    }
                  >
                    {Object.keys(safeObject(fontsData[data.styles[keyFamily]])).map((style) => (
                      <SingleSelectOption value={style} key={style}>
                        {style}
                      </SingleSelectOption>
                    ))}
                  </SingleSelect>
                )}
                {data.styles[keyFamily] &&
                  safeObject(fontsData[data.styles[keyFamily]]) &&
                  data.styles[keyWeight] && (
                    <SingleSelect
                      label={t("uICustomizationFonts.fontWeight")}
                      value={data.styles[keyWeight]}
                      onChange={(item) =>
                        debouncedOnChange({
                          ...data,
                          styles: {
                            ...data.styles,
                            [keyWeight]: item,
                          },
                        })
                      }
                    >
                      {safeObject(
                        fontsData[data.styles[keyFamily]]?.[data.styles[keyStyle]] ?? []
                      ).map((style) => (
                        <SingleSelectOption value={style} key={style}>
                          {style}
                        </SingleSelectOption>
                      ))}
                    </SingleSelect>
                  )}
              </Flex>
            </Box>
          ))}
        </GridLayout>
      )}
    </>
  )
}
